/**
 * Live preview: the same engine Renderer the worker uses, drawing into the stage canvas.
 *
 * The canvas is created with `willReadFrequently`, which keeps Chromium on its CPU rasteriser,
 * like the headless render worker; at equal resolution, preview and render are pixel-identical.
 */

import {
  elementTimes,
  emptyResources,
  frameCount,
  loadAsset,
  projectDuration,
  Renderer,
  usedAssetIds,
  type Asset,
  type CameraKey,
  type CameraState,
  type Project,
  type Rect,
  type Resources,
} from "@studio/engine";
import { filesBase, type AnalysisState } from "../api";
import { AudioPlayer } from "./audio";
import { useEditor } from "./store";

const LOAD_AT_ONCE = 6;

export class Preview {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private renderer: Renderer | null = null;
  private resources: Resources = emptyResources();
  private loaded = new Map<string, string>();
  private loading = new Set<string>();
  readonly audio: AudioPlayer;
  private raf = 0;
  private drawQueued = false;
  private listeners = new Set<() => void>();
  readonly base: string;
  /** bumped whenever element sizes may have changed (resources loaded) */
  version = 0;

  constructor(readonly pid: string) {
    this.base = filesBase(pid);
    this.canvas = document.createElement("canvas");
    this.canvas.className = "stage-canvas";
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true, alpha: false })!;
    this.audio = new AudioPlayer(this.base);
  }

  get width() {
    return this.canvas.width;
  }
  get height() {
    return this.canvas.height;
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  setSize(w: number, h: number): void {
    w = Math.max(16, Math.round(w));
    h = Math.max(9, Math.round(h));
    if (w === this.canvas.width && h === this.canvas.height) return;
    this.canvas.width = w;
    this.canvas.height = h;
    this.renderer?.setTarget({ ctx: this.ctx, width: w, height: h });
    this.requestDraw();
  }

  setProject(p: Project): void {
    if (!this.renderer) this.renderer = new Renderer(p, this.resources, { ctx: this.ctx, width: this.canvas.width, height: this.canvas.height });
    else this.renderer.setProject(p);
    this.emit();
    this.requestDraw();
  }

  /** Load render data for every used asset whose analysis is ready and newer than what we have:
   *  reduced editor copies of the layers, a few at a time, the ones used nearest the playhead
   *  first (so the scene on screen appears quickly and thumbnails aren't stuck behind the rest). */
  sync(p: Project, analysis: Record<string, AnalysisState>): void {
    const used = usedAssetIds(p);
    const want: { a: Asset; key: string }[] = [];
    for (const a of p.assets) {
      if (!used.has(a.id) || this.loading.has(a.id)) continue;
      let key: string | null = null;
      if (a.kind === "image" || a.kind === "svg" || a.kind === "font") {
        const st = analysis[a.id];
        if (!st?.ready || st.stamp === undefined) continue;
        key = String(st.stamp);
      } else if (a.kind === "hand") key = a.file;
      else continue;
      if (this.loaded.get(a.id) !== key) want.push({ a, key });
    }
    if (want.length) {
      const now = useEditor.getState().time;
      const times = elementTimes(p);
      const firstUse = new Map<string, number>();
      for (const el of p.elements) {
        const id = el.image?.assetId ?? el.text?.fontAssetId;
        const t = times.get(el.id)?.enter0;
        if (id && t !== undefined) firstUse.set(id, Math.min(firstUse.get(id) ?? Infinity, Math.abs(t - now) - (t >= now ? 0.5 : 0)));
      }
      // fonts and hands first (small, needed everywhere), then images by distance from the playhead
      const rank = (x: { a: Asset }) => (x.a.kind === "image" || x.a.kind === "svg" ? firstUse.get(x.a.id) ?? Infinity : -1);
      this.queue = want.sort((x, y) => rank(x) - rank(y));
    }
    this.pump();
    // background image: the uploaded file itself, no analysis needed
    const bg = p.board.background;
    const bgAsset = bg.kind === "image" && bg.assetId ? p.assets.find((a) => a.id === bg.assetId) : undefined;
    if (bgAsset && this.loaded.get(`bg:${bgAsset.id}`) !== bgAsset.file && !this.loading.has(`bg:${bgAsset.id}`)) {
      const key = `bg:${bgAsset.id}`;
      this.loading.add(key);
      loadAsset(bgAsset, this.base, this.resources, undefined, true)
        .then(() => {
          this.loaded.set(key, bgAsset.file);
          this.requestDraw();
        })
        .catch((e) => console.warn("background load failed", e))
        .finally(() => this.loading.delete(key));
    }
    void this.audio.ensure(p);
  }

  private queue: { a: Asset; key: string }[] = [];

  private pump(): void {
    while (this.loading.size < LOAD_AT_ONCE && this.queue.length) {
      const { a, key } = this.queue.shift()!;
      if (this.loading.has(a.id) || this.loaded.get(a.id) === key) continue;
      this.loading.add(a.id);
      loadAsset(a, this.base, this.resources, key, false, true)
        .then(() => {
          this.loaded.set(a.id, key);
          this.renderer?.invalidateAsset(a.id);
          this.emit();
          this.requestDraw();
        })
        .catch((e) => console.warn("load failed", a.id, e))
        .finally(() => {
          this.loading.delete(a.id);
          this.pump();
        });
    }
  }

  elementSize(id: string) {
    return this.renderer?.elementSize(id) ?? null;
  }

  boardBounds(id: string): Rect | null {
    return this.renderer?.boardBounds(id) ?? null;
  }

  /** The project camera at t (auto-camera resolved). */
  cameraAt(t: number): CameraState {
    return this.renderer?.cameraAt(t) ?? { x: 960, y: 540, zoom: 1, rotation: 0 };
  }

  cameraKeys(): { auto: CameraKey[]; resolved: CameraKey[] } {
    return this.renderer?.cameraKeys ?? { auto: [], resolved: [] };
  }

  /** Camera that shows the whole board (board view). */
  boardCamera(): CameraState {
    const p = useEditor.getState().project!;
    const zoom = Math.min(p.output.width / p.board.width, p.output.height / p.board.height) * 0.96;
    return { x: p.board.width / 2, y: p.board.height / 2, zoom, rotation: 0 };
  }

  /** The camera the stage shows: the board view or the project camera. */
  viewCamera(t: number): CameraState {
    return useEditor.getState().boardView ? this.boardCamera() : this.cameraAt(t);
  }

  requestDraw(): void {
    if (this.drawQueued) return;
    this.drawQueued = true;
    requestAnimationFrame(() => {
      this.drawQueued = false;
      if (!useEditor.getState().playing) this.draw(useEditor.getState().time);
    });
  }

  draw(t: number): void {
    this.renderer?.renderFrame(t, useEditor.getState().boardView ? this.boardCamera() : undefined);
  }

  /** Render frame t with a fresh renderer at w × h (the preview's own pixels, for comparisons). */
  renderOffscreen(t: number, w: number, h: number): ImageData {
    const p = useEditor.getState().project!;
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d", { willReadFrequently: true, alpha: false })!;
    new Renderer(p, this.resources, { ctx, width: w, height: h }).renderFrame(t);
    return ctx.getImageData(0, 0, w, h);
  }

  duration(): number {
    const p = useEditor.getState().project;
    return p ? projectDuration(p) : 0;
  }

  async play(): Promise<void> {
    const st = useEditor.getState();
    if (!st.project || st.playing) return;
    const dur = this.duration();
    let t0 = st.time;
    if (t0 >= dur - 0.05) t0 = 0;
    st.setPlaying(true);
    await this.audio.start(st.project, t0, dur);
    const tick = () => {
      if (!useEditor.getState().playing) return;
      const t = this.audio.now();
      if (t >= dur) {
        useEditor.getState().setTime(dur);
        this.pause();
        return;
      }
      useEditor.getState().setTime(t);
      this.draw(t);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  pause(): void {
    cancelAnimationFrame(this.raf);
    this.audio.stop();
    useEditor.getState().setPlaying(false);
    this.requestDraw();
  }

  toggle(): void {
    if (useEditor.getState().playing) this.pause();
    else void this.play();
  }

  /** Snap a time to the frame grid. */
  frameTime(t: number): number {
    const fps = useEditor.getState().project?.output.fps ?? 30;
    return Math.round(t * fps) / fps;
  }

  frames(): number {
    const p = useEditor.getState().project;
    return p ? frameCount(this.duration(), p.output.fps) : 0;
  }
}
